import Moment from 'moment';

import { getDateFormatForLocale } from './hooks/useDateFormat';

/**
 * Helpers for date fields the user types into, as opposed to picks from a
 * calendar. Parsing is strict and locale-aware: a value is only accepted when
 * it is a complete, real date in the user's own format, so a typed entry can
 * never be silently coerced into a different day than the one intended.
 */

const MAX_DATE_DIGITS = 8;
const DAY_MONTH_LENGTH = 2;
const YEAR_LENGTH = 4;

export type ParsedDateInput =
  | { status: 'empty' }
  | { status: 'invalid' }
  | { status: 'ok'; date: Date };

export type DateBoundsCheck = 'ok' | 'before-min' | 'after-max';

/**
 * Progressively formats numeric entry into the user's locale date format,
 * inserting the separators as they type.
 *
 * Separators are only ever rendered between groups, never trailing, so
 * backspace always removes a digit rather than a slash that reappears.
 */
export function maskDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, MAX_DATE_DIGITS);

  const firstGroupEnd = DAY_MONTH_LENGTH;
  const secondGroupEnd = DAY_MONTH_LENGTH * 2;
  const yearGroupEnd = secondGroupEnd + YEAR_LENGTH;

  return [
    digits.slice(0, firstGroupEnd),
    digits.slice(firstGroupEnd, secondGroupEnd),
    digits.slice(secondGroupEnd, yearGroupEnd),
  ]
    .filter((group) => group.length > 0)
    .join('/');
}

/**
 * The locale format plus the looser variants a user may reasonably type:
 * single-digit day/month, and a two digit year.
 */
function candidateDateFormats(): string[] {
  const localeFormat = getDateFormatForLocale();
  const singleDigitFormat = localeFormat.replace('MM', 'M').replace('DD', 'D');

  return [
    localeFormat,
    singleDigitFormat,
    localeFormat.replace('YYYY', 'YY'),
    singleDigitFormat.replace('YYYY', 'YY'),
  ];
}

/**
 * Parses a typed date in the user's locale format. Strict throughout — a
 * partial entry, an impossible day such as 02/30, or anything non-numeric is
 * rejected rather than being coerced to a nearby valid date.
 */
export function parseTypedDate(input: string): ParsedDateInput {
  const value = input.trim();

  if (!value) {
    return { status: 'empty' };
  }

  for (const format of candidateDateFormats()) {
    const parsed = Moment(value, format, true);

    if (parsed.isValid()) {
      return { status: 'ok', date: parsed.startOf('day').toDate() };
    }
  }

  return { status: 'invalid' };
}

/**
 * Reports which bound a date falls outside of, so callers can build a message
 * naming the offending end of the range.
 */
export function checkDateBounds(
  date: Date,
  minDate?: Date,
  maxDate?: Date
): DateBoundsCheck {
  const day = Moment(date).startOf('day');

  if (minDate && day.isBefore(Moment(minDate).startOf('day'))) {
    return 'before-min';
  }

  if (maxDate && day.isAfter(Moment(maxDate).startOf('day'))) {
    return 'after-max';
  }

  return 'ok';
}

export function isWithinRange(
  date: Date,
  minDate?: Date,
  maxDate?: Date
): boolean {
  return checkDateBounds(date, minDate, maxDate) === 'ok';
}

/** The placeholder/hint text for a typed date field, e.g. "MM/DD/YYYY". */
export function getDateInputPlaceholder(): string {
  return getDateFormatForLocale();
}
